import { useState, useRef, useEffect, forwardRef, KeyboardEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, CheckboxField, Chip, SearchField, Text, useFormField, useTooltip } from '@frontend/design-system';
import { teamPresenceTrackingId } from '../../pendo-tracking';
import { DEVICE_STATE_CHIP } from './const';
import { TourHighlight } from './tour-highlight';
import { CardProps, SelectableCardProps } from './types';
import { formatDuration } from './utils';

dayjs.extend(dayjsDuration);

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ id, nameplate, avatar, body, actions, meta = [] }, ref) => {
    return (
      <div
        ref={ref}
        css={{
          display: 'grid',
          backgroundColor: theme.colors.white,
          rowGap: theme.spacing(1),
          columnGap: theme.spacing(1),
          gridTemplateAreas: '"avatar name-plate actions" ". body body" ". meta meta"',
          gridTemplateColumns: 'max-content 1fr auto',
          gridTemplateRows: 'max-content max-content max-content',
          borderRadius: theme.borderRadius.medium,
          border: `1px solid ${theme.colors.neutral20}`,
          minWidth: 326,
          width: '100%',
          padding: theme.spacing(2),
          alignItems: 'start',
        }}
      >
        <div css={{ gridArea: 'avatar' }}>{avatar}</div>
        <div css={{ gridArea: 'name-plate', overflow: 'hidden' }}>{nameplate}</div>
        <div
          css={{
            gridArea: 'actions',
            display: 'grid',
            gridAutoFlow: 'column',
            gap: theme.spacing(1),
            justifyContent: 'end',
            width: 'max-content',
            alignItems: 'center',
          }}
        >
          {actions.map(({ label, icon, onClick, disabled, tooltip, callout, element, trackingId }) =>
            element ? (
              element
            ) : (
              <TourHighlight
                key={`${id}-${icon}-${label}`}
                dotId={`card-${id}-action-${icon}`}
                message={callout}
                placement='top'
                containerCss={{ display: 'inline-flex' }}
                dotPosition={{ top: 5, right: 5 }}
              >
                <ActionButton
                  label={label}
                  icon={icon}
                  onClick={onClick}
                  disabled={disabled}
                  tooltip={tooltip}
                  trackingId={trackingId}
                />
              </TourHighlight>
            )
          )}
        </div>
        <div
          css={{
            gridArea: 'body',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(0, max-content))',
            gap: theme.spacing(1),
          }}
        >
          {body}
        </div>
        <div css={{ gridArea: 'meta' }}>{meta}</div>
      </div>
    );
  }
);

const ActionButton = ({
  label,
  icon,
  onClick,
  disabled,
  tooltip,
  trackingId,
}: {
  label: string;
  icon: IconName;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  trackingId?: string;
}) => {
  return tooltip ? (
    <ButtonWithTooltip
      label={label}
      icon={icon}
      onClick={onClick}
      disabled={disabled}
      tooltip={tooltip}
      trackingId={trackingId}
    />
  ) : (
    <Button
      size='small'
      label={label}
      iconName={icon}
      variant='secondary'
      css={{ width: 24, height: 24, padding: 4, svg: { width: 16, height: 16 } }}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      onPointerDown={(e) => e.stopPropagation()}
      trackingId={trackingId}
    />
  );
};

const ButtonWithTooltip = ({
  label,
  icon,
  onClick,
  disabled,
  tooltip,
  trackingId,
}: {
  label: string;
  icon: IconName;
  onClick: () => void;
  disabled?: boolean;
  tooltip: string;
  trackingId?: string;
}) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({
    trigger: 'hover',
  });

  return (
    <>
      <Button
        {...triggerProps}
        size='small'
        label={label}
        iconName={icon}
        variant='secondary'
        css={{ width: 24, height: 24, padding: 4, svg: { width: 16, height: 16 } }}
        onClick={onClick}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        onPointerDown={(e) => e.stopPropagation()}
        trackingId={trackingId}
      />
      <Tooltip {...tooltipProps}>{tooltip}</Tooltip>
    </>
  );
};

Card.displayName = 'Card';

export const LocationChip = ({ locations }: { locations: string[] }) => {
  const { t } = useTranslation('phone');
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ trigger: 'hover' });

  if (locations.length === 0) return null;

  if (locations.length === 1) {
    return <Chip.Location>{locations[0]}</Chip.Location>;
  }

  return (
    <>
      <span
        {...triggerProps}
        onClick={() => {
          pendo.track(teamPresenceTrackingId({ context: 'card-location-chip::dead-end' }));
        }}
      >
        <Chip.MultiChip>{t('{{count}} Locations', { count: locations.length })}</Chip.MultiChip>
      </span>
      <Tooltip {...tooltipProps}>
        {locations.map((location) => (
          <div key={location}>{location}</div>
        ))}
      </Tooltip>
    </>
  );
};

export const CardBody = ({
  cardId,
  deviceState,
  locations,
  showLocations,
  tooltip,
}: {
  cardId: string;
  deviceState: keyof typeof DEVICE_STATE_CHIP;
  locations: { id: string; name: string }[];
  showLocations?: boolean;
  tooltip?: string;
}) => {
  return (
    <>
      <TourHighlight dotId={`card-device-state-${cardId}`} message={tooltip} placement='top'>
        <div
          onClick={() => {
            pendo.track(teamPresenceTrackingId({ context: 'card-device-state::dead-end' }));
          }}
        >
          {DEVICE_STATE_CHIP[deviceState].chip}
        </div>
      </TourHighlight>
      {showLocations && locations && <LocationChip locations={locations.map((loc) => loc.name)} />}
    </>
  );
};

function useLiveDuration(initialMs: number) {
  const [duration, setDuration] = useState(initialMs);
  const startTimeRef = useRef(Date.now() - initialMs);

  useEffect(() => {
    if (initialMs === 0) return;

    // Update the start time when initialMs changes
    startTimeRef.current = Date.now() - initialMs;
    setDuration(initialMs);

    const interval = setInterval(() => {
      setDuration(Date.now() - startTimeRef.current);
    }, 1000);
    return () => clearInterval(interval);
  }, [initialMs]);

  return duration;
}

export const DurationComponent = ({ deviceActivity }: { deviceActivity: { state: string; timestamp?: string } }) => {
  const { t } = useTranslation('phone');
  const duration = useLiveDuration(deviceActivity.timestamp ? dayjs().diff(dayjs(deviceActivity.timestamp), 'ms') : 0);
  const durationLabel = duration
    ? formatDuration(duration, {
        showSeconds: deviceActivity.state === 'active',
      })
    : t('No Data Available');

  let label = '';
  if (duration === 0) {
    label =
      deviceActivity.state === 'active'
        ? t('Call duration: {{duration}}', { duration: durationLabel })
        : t('Last call: {{duration}}', { duration: durationLabel });
  } else {
    label =
      deviceActivity.state === 'active'
        ? t('Call duration: {{duration}}', { duration: durationLabel })
        : t('Last call: {{duration}} ago', { duration: durationLabel });
  }

  return (
    <div
      css={{
        gridArea: 'time',
        display: 'grid',
        gridTemplateColumns: `auto 1fr`,
        gap: theme.spacing(1),
        alignItems: 'center',
      }}
      onClick={() => {
        pendo.track(teamPresenceTrackingId({ context: 'card-duration::dead-end' }));
      }}
    >
      <Icon name='clock-small' color='light' />
      <Text size='medium'>{label}</Text>
    </div>
  );
};

export const SelectableCard = ({
  avatar,
  nameplate,
  meta,
  onChange,
  isSelected,
  disabled,
  disabledTooltip,
}: SelectableCardProps) => {
  const field = useFormField({ type: 'checkbox', value: isSelected }, [isSelected]);
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ trigger: 'hover' });
  const showTooltip = disabled && !!disabledTooltip;

  const toggleSelection = () => {
    if (disabled) return;
    const nextValue = !field.value;
    onChange(nextValue);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      toggleSelection();
    }
  };

  return (
    <>
      <div
        {...(showTooltip ? triggerProps : {})}
        css={{
          display: 'grid',
          rowGap: theme.spacing(1),
          columnGap: theme.spacing(1),
          gridTemplateAreas: '"checkbox avatar name-plate" ". . meta"',
          gridTemplateColumns: 'max-content auto 1fr',
          gridTemplateRows: 'max-content max-content',
          borderRadius: theme.borderRadius.medium,
          border: `1px solid`,
          borderColor: field.value ? theme.colors.primary50 : theme.colors.neutral20,
          minWidth: 260,
          width: '100%',
          height: 108,
          padding: theme.spacing(2),
          alignItems: 'center',
          outline: field.value ? `1px solid ${theme.colors.primary50}` : 'none',
          cursor: 'pointer',
          ...(disabled && { opacity: 0.65, cursor: 'not-allowed' }),
          ...(!disabled && {
            ':hover': {
              backgroundColor: theme.colors.neutral5,
            },
          }),
        }}
        role='checkbox'
        aria-checked={field.value}
        onClick={toggleSelection}
        onKeyDown={handleKeyDown}
      >
        <div
          css={{ gridArea: 'checkbox' }}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <CheckboxField
            {...field}
            disabled={disabled}
            onChange={(e) => {
              field.onChange(e);
              // @ts-expect-error - event type incorrect
              onChange(e.value);
            }}
            name='checkbox'
            label=''
          />
        </div>
        <div css={{ gridArea: 'avatar' }}>{avatar}</div>
        <div css={{ gridArea: 'name-plate', overflow: 'hidden' }}>{nameplate}</div>
        <div css={{ gridArea: 'meta' }}>{meta}</div>
      </div>
      {showTooltip && <Tooltip {...tooltipProps}>{disabledTooltip}</Tooltip>}
    </>
  );
};

export const CardSearch = ({
  value = '',
  onChange,
  onDismiss,
  onOpen,
  isExpanded,
  useCompactMode,
  className,
}: {
  value?: string;
  onChange: (nextSearch: string) => void;
  onDismiss?: () => void;
  onOpen?: () => void;
  isExpanded?: boolean;
  useCompactMode?: boolean;
  className?: string;
}) => {
  const field = useFormField({ type: 'text', value });

  const showSearchButton = useCompactMode && !isExpanded;
  const showDismissButton = useCompactMode && isExpanded;

  const handleClear = () => {
    onChange('');
    onDismiss?.();
  };

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: showDismissButton ? '1fr auto' : '1fr',
        gap: theme.spacing(1),
        alignItems: 'center',
        width: '100%',
      }}
      className={className}
    >
      {showSearchButton ? (
        <Button
          variant='secondary'
          size='large'
          iconName='search'
          onClick={() => {
            onOpen?.();
          }}
        />
      ) : (
        <SearchField
          {...field}
          onChange={(e) => {
            field.onChange(e);
            if (e && 'target' in e) {
              // @ts-expect-error - event type incorrect
              onChange(e.target.value);
            }
          }}
          onClear={handleClear}
          name='card-search-field'
          css={{
            width: '100%',
          }}
        />
      )}
      {showDismissButton && (
        <Button variant='secondary' size='large' onClick={handleClear}>
          {`Clear`}
        </Button>
      )}
    </div>
  );
};

export const DraggableCard = (props: CardProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: props.id,
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.25 : 1,
      }
    : undefined;

  return (
    <div
      css={{
        borderRadius: theme.borderRadius.medium,
      }}
      style={style}
      {...listeners}
      {...attributes}
      ref={setNodeRef}
    >
      <Card {...props} />
    </div>
  );
};
